import { supabase } from "@/lib/supabase";
import { handleError, ErrorContext } from "@/lib/utils/error-handler";

export interface DeletionStatus {
  isScheduled: boolean;
  scheduledAt: Date | null;
  daysRemaining: number | null;
}

/**
 * Account Deletion Service
 * Single Responsibility: Manage account deletion scheduling and cancellation
 * KISS: Simple service with clear methods
 * DRY: Reuses error handling patterns from auth.service.ts
 */
export const accountDeletionService = {
  /**
   * Schedule account deletion (1 week from now)
   * Sets deletion_scheduled_at to NOW() + 7 days
   */
  async scheduleDeletion(): Promise<void> {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      throw new Error("User not authenticated");
    }

    // Calculate deletion date (1 week from now)
    const deletionDate = new Date();
    deletionDate.setDate(deletionDate.getDate() + 7);

    const { error } = await supabase
      .from("profiles")
      .update({
        deletion_scheduled_at: deletionDate.toISOString(),
        deletion_cancelled_at: null, // Clear any previous cancellation
      })
      .eq("id", user.id);

    if (error) {
      const context: ErrorContext = {
        action: "scheduleDeletion",
        component: "accountDeletionService",
        userId: user.id,
      };
      handleError(error, context, { showToast: false, silent: false });
      throw new Error(error.message);
    }
  },

  /**
   * Cancel scheduled account deletion
   * Sets deletion_cancelled_at to NOW() and deletion_scheduled_at to NULL
   */
  async cancelDeletion(): Promise<void> {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      throw new Error("User not authenticated");
    }

    const { error } = await supabase
      .from("profiles")
      .update({
        deletion_scheduled_at: null,
        deletion_cancelled_at: new Date().toISOString(),
      })
      .eq("id", user.id);

    if (error) {
      const context: ErrorContext = {
        action: "cancelDeletion",
        component: "accountDeletionService",
        userId: user.id,
      };
      handleError(error, context, { showToast: false, silent: false });
      throw new Error(error.message);
    }
  },

  /**
   * Cancel deletion if scheduled (used during sign in)
   * Only cancels if deletion_scheduled_at is not null
   */
  async cancelDeletionIfScheduled(): Promise<void> {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      return; // Silently fail if user not authenticated
    }

    // Check if deletion is scheduled
    const { data: profile, error: fetchError } = await supabase
      .from("profiles")
      .select("deletion_scheduled_at")
      .eq("id", user.id)
      .single();

    if (fetchError || !profile) {
      return; // Silently fail
    }

    // Only cancel if deletion is scheduled
    if (profile.deletion_scheduled_at) {
      await this.cancelDeletion();
    }
  },

  /**
   * Get current deletion status
   * Returns whether deletion is scheduled, when, and days remaining
   */
  async getDeletionStatus(): Promise<DeletionStatus> {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      return {
        isScheduled: false,
        scheduledAt: null,
        daysRemaining: null,
      };
    }

    const { data: profile, error } = await supabase
      .from("profiles")
      .select("deletion_scheduled_at")
      .eq("id", user.id)
      .single();

    if (error || !profile || !profile.deletion_scheduled_at) {
      return {
        isScheduled: false,
        scheduledAt: null,
        daysRemaining: null,
      };
    }

    const scheduledAt = new Date(profile.deletion_scheduled_at);
    const now = new Date();
    const diffTime = scheduledAt.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return {
      isScheduled: true,
      scheduledAt,
      daysRemaining: diffDays > 0 ? diffDays : 0,
    };
  },

  /**
   * Delete account permanently (called by Edge Function)
   * This should only be called by the Edge Function with proper authentication
   */
  async deleteAccount(userId: string): Promise<void> {
    // This method is for internal use by Edge Function
    // In production, this should be protected and only callable by Edge Functions
    const { error } = await supabase.auth.admin.deleteUser(userId);

    if (error) {
      throw new Error(`Failed to delete account: ${error.message}`);
    }
  },
};

